/* eslint-disable no-nested-ternary */
import PropTypes from 'prop-types';
import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import { defineMessages, injectIntl } from 'react-intl';
import { withRouter } from 'react-router-dom';
import zipState from 'zip-state';
import Meta from 'shared/containers/MetaContainer';
import Bandeau from 'brastrap/common/bandeau/Bandeau';
import Grid from 'brastrap/containers/grid/GridContainer';
import EditorialContainer from 'brastrap/editorial/editorial-container/EditorialContainer';
import ReturnsItemSelection from '../../../../../../brastrap/components/transactional/returns-portal/ReturnsItemSelection';
import ReturnsOrderSearch from '../../../../../../brastrap/components/transactional/returns-portal/ReturnsOrderSearch';
import {
  searchForOrderToReturn,
  requestReturn,
} from '../../../actions/returns';
import { getWrapperContainers } from '../../../../../actions/editorial';

export const SETTING_GROUP_START = 'start-return';
export const SETTING_GROUP_MID = 'mid-return';
export const SETTING_GROUP_NY = 'ny-return';

const messages = defineMessages({
  title: {
    id: 'returns.title',
    defaultMessage: 'Returns',
  },
  return_expired: {
    id: 'returns.errorMsgPrefix',
    defaultMessage:
      'We’re sorry, that order was placed too long ago to be returned. If you need any further help you can contact our Customer Service Team tollfree at 1 877 826 3857.ed',
  },
});

const StartReturnPageContainer = (props: Props) => {
  const [searchResult, setSearchResult] = useState(null);
  const [orderNo, setOrderNo] = useState(null);
  const [zipCode, setZipCode] = useState(null);
  const [orderId, setOrderId] = useState(null);
  const [orderToReturn, setOrderToReturn] = useState(null);
  const [loading, setLoading] = useState(false);
  const [invalidOrderErrorMsg, setInvalidOrderErrorMsg] = useState(null);
  const [requestReturnErrorMsg, setRequestReturnErrorMsg] = useState(null);
  const [settingGroup, setSettingGroup] = useState(SETTING_GROUP_START);

  const {
    intl: { formatMessage },
    getContainers,
    headerContainers,
    footerContainers,
    onSearchForOrder,
    onRequestReturn,
    history,
    isReturnsPortalEnabled,
  } = props;

  const zipCodesMap = {
    NY: 'NY',
    NJ: 'NJ',
    CT: 'CT',
  };

  const settingGroupSelector = region => {
    if (region === zipCodesMap[region]) {
      setSettingGroup(SETTING_GROUP_NY);
    } else {
      setSettingGroup(SETTING_GROUP_MID);
    }
  };

  useEffect(() => {
    getContainers(settingGroup);
  }, [settingGroup]);

  useEffect(() => {
    (async () => {
      const region = zipState(zipCode);
      if (zipCode && orderNo) {
        const response = await onSearchForOrder(zipCode, orderNo, setLoading);
        if (response.message) {
          setInvalidOrderErrorMsg(response.message);
        } else if (!response.isReturnable) {
          setInvalidOrderErrorMsg(
            formatMessage(
              messages[`${response.notReturnableReason}`.toLowerCase()]
            )
          );
        } else if (!loading) {
          setSearchResult(response);
          settingGroupSelector(region);
        }
      }
    })();
  }, [zipCode, orderNo]);

  useEffect(() => {
    (async () => {
      if (orderId && orderToReturn) {
        const response = await onRequestReturn(
          orderId,
          orderToReturn,
          setLoading
        );
        if (response.message) {
          setRequestReturnErrorMsg(response.message);
        } else if (!loading) {
          history.push('/us/returns/confirmation');
        }
      }
    })();
  }, [orderId, orderToReturn]);

  return (
    isReturnsPortalEnabled && (
      <Meta title={formatMessage(messages.title)}>
        <Bandeau title={formatMessage(messages.title)} />
        <div className="c-page__main">
          <Grid>
            <Grid.Content>
              {headerContainers && !!headerContainers.length && (
                <EditorialContainer containers={headerContainers} />
              )}
              {searchResult ? (
                <ReturnsItemSelection
                  result={searchResult}
                  orderNo={orderNo}
                  onRequestReturn={(orderID, order) => {
                    setOrderId(orderID);
                    setOrderToReturn(order);
                  }}
                  isLoading={loading}
                  requestReturnErrorMsg={requestReturnErrorMsg}
                />
              ) : (
                <ReturnsOrderSearch
                  onOrderSearch={(zipcode, orderNumber) => {
                    setZipCode(zipcode);
                    setOrderNo(orderNumber);
                  }}
                  isLoading={loading}
                  invalidOrderErrorMsg={invalidOrderErrorMsg}
                  setInvalidOrderErrorMsg={setInvalidOrderErrorMsg}
                />
              )}
              {footerContainers && !!footerContainers.length && (
                <EditorialContainer containers={footerContainers} />
              )}
            </Grid.Content>
          </Grid>
        </div>
      </Meta>
    )
  );
};

StartReturnPageContainer.propTypes = {
  intl: PropTypes.shape({
    formatMessage: PropTypes.func.isRequired,
  }),
  getContainers: PropTypes.func.isRequired,
  headerContainers: PropTypes.array,
  footerContainers: PropTypes.array,
};

const mapStateToProps = state => ({
  headerContainers: state.content.headerContainers || [],
  footerContainers: state.content.footerContainers || [],
  isReturnsPortalEnabled: state.app.config.returnsPortal.enabled,
});

const mapDispatchToProps = dispatch => ({
  getContainers: settingGroup => dispatch(getWrapperContainers(settingGroup)),
  onSearchForOrder: (zipCode, orderNo, setLoading) =>
    dispatch(searchForOrderToReturn(zipCode, orderNo, setLoading)),
  onRequestReturn: (orderId, orderToReturn, setLoading) =>
    dispatch(requestReturn(orderId, orderToReturn, setLoading)),
});

StartReturnPageContainer.populateStore = async store => {
  store.dispatch(getWrapperContainers(SETTING_GROUP_START));
};

const WrappedStartReturnPage = injectIntl(
  withRouter(
    connect(mapStateToProps, mapDispatchToProps)(StartReturnPageContainer)
  )
);

export default connect()(injectIntl(WrappedStartReturnPage));
